/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { AfterViewInit, Component, ElementRef, inject, Input, ViewContainerRef } from '@angular/core';
import { ValidatorFn, AsyncValidatorFn, FormGroup, UntypedFormGroup } from '@angular/forms';
import { AbstractObject } from '@src/app/model/abstract-object';
import { Field } from '@src/app/shared/field/Field';
import { GridField } from '@src/app/shared/grid/grid-field';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { AppFormControl, ControlOn } from '@src/app/shared/form/app-form-control';
import { AttachmentField } from "@src/app/shared/field/AttachmentField";
import { CtlHolderComponent } from '@src/app/shared/form/ctl-holder/ctl-holder.component';
import { MatTabsModule } from '@angular/material/tabs';
import { NgClass, NgTemplateOutlet } from '@angular/common';

export const LAYOUT_OPTIONS = {
    simpleDialog: [{ cells: [{}] }],
    singleCol: [{ cells: [{}] }, { cells: [{}] }],

    doubleCol: [{ cells: [{ width: '50%' }, { width: '50%' }] }],
    doubleColOver1: [{ cells: [{ width: '50%' }, { width: '50%' }] },
    { cells: [{ colspan: 2, pageTab: 'yes' }] }],

    threeCol: [{ cells: [{ width: '33%' }, { width: '33%' }, { width: '33%' }] }],
    threeColOver1: [{ cells: [{ width: '33%' }, { width: '33%' }, { width: '33%' }] },
    { cells: [{ colspan: 3, pageTab: 'yes' }] }],

    fourCol: [{ cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] }],
    fourColOver1: [{ cells: [{ width: '25%' }, { width: '25%' }, { width: '25%' }, { width: '25%' }] },
    { cells: [{ colspan: 4, pageTab: 'yes' }] }],

    fiveCol: [{ cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] }],
    fiveColOver1: [{ cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '20%' }] },
    { cells: [{ colspan: 5, pageTab: 'yes' }] },
    ]
};

export interface CellDef {
    width?: string;
    colspan?: number;
    rowspan?: number;
    style?: string;
    pageTab?: string;
    showAfter?: boolean;
    class?: string;
}
export interface RowDef {
    showAfter?: boolean,
    cells: CellDef[];
}

export class FieldSet {
    fields: Field[] = [];
    formLayout: RowDef[] = LAYOUT_OPTIONS.threeColOver1;
    formValidator: ValidatorFn[];
    asyncFormValidator: AsyncValidatorFn[];
    valueChanges: (newValue, formGroup) => void;
    formGroup: FormGroup;

    private cachedLines: Field[][] = null;
    private cachedFieldCount = 0;

    constructor(defaults: Partial<FieldSet> = {}, initFormGroup = false) {
        Object.getOwnPropertyNames(defaults).forEach(prop => {
            this[prop] = defaults[prop];
        });
        if (initFormGroup) {
            this.setFormGroup();
        }
    }
    setFields(fields: Field[]) {
        this.fields = fields;
        this.setFormGroup();
    }

    /**
     * The fields grouped into the lines the form draws them on. A field with no `formLine` is a
     * line of its own - which is every field unless a page says otherwise - so the layout is
     * unchanged until a token is added. Cached rather than rebuilt on every change detection pass;
     * the template tracks a line by its first field, so the rendered controls survive a rebuild.
     * Grouping is static (cell + token), so visibility changes need no rebuild - inRowCol filters
     * at render time as it always did. A few pages push extra fields straight onto `fields` rather
     * than calling setFields, so the count is checked too.
     */
    get lines(): Field[][] {
        if (!this.cachedLines || this.cachedFieldCount !== this.fields.length) {
            this.cachedFieldCount = this.fields.length;
            this.cachedLines = this.buildLines();
        }
        return this.cachedLines;
    }

    /* Keyed by cell as well as token, so a token shared across two cells cannot drag a field out
       of the cell its formRow / formColumn put it in - it just fails to pair, as it does today. */
    private buildLines(): Field[][] {
        const lines: Field[][] = [];
        const byToken = new Map<string, Field[]>();

        this.fields.forEach(field => {
            if (!field.formLine) {
                lines.push([field]);
                return;
            }
            const key = `${field.formRow}:${field.formColumn}:${field.formLine}`;
            let line = byToken.get(key);
            if (!line) {
                line = [];
                byToken.set(key, line);
                lines.push(line); // the line renders where its first member was declared
            }
            line.push(field);
        });
        this.warnOnSplitLines(lines);
        return lines;
    }

    private warnOnSplitLines(lines: Field[][]) {
        const seen = new Map<string, Field>();
        lines.forEach(line => {
            const token = line[0].formLine;
            if (!token) {
                return;
            }
            const first = seen.get(token);
            if (first) {
                console.warn('formLine members must share a row and column - they will not pair up',
                    { line: token, first, stray: line[0] });
            } else {
                seen.set(token, line[0]);
            }
        });
    }

    setFormGroup() {
        this.cachedLines = null;
        this.formGroup = new UntypedFormGroup({}, this.formValidator, this.asyncFormValidator);

        this.fields.forEach(field => {
            this.formGroup.addControl(field.name, field.makeControl());
        });

        if (this.valueChanges) {
            this.formGroup.valueChanges.subscribe((newValue) => {
                this.valueChanges(newValue, this.formGroup);
            });
        }
    }

    getFormGroup() {
        if (!this.formGroup) {
            this.setFormGroup();
        }
        return this.formGroup;
    }

    setValue(item: AbstractObject, readonly = false) {
        this.getFormGroup(); // Make sure controls are initialized...
        this.fields.forEach(f => f.setValue(item, readonly));
    }

    getFormValue(getAll = false): AbstractObject {
        const formValue = {} as AbstractObject;
        this.fields.forEach(field => {
            if (field.sendServer || getAll) {

                if (field instanceof GridField) {
                    formValue[field.name] = (field.control as GridControl).getFormValue(getAll);
                } else {
                    //formValue[field.name] = field.getFormValue();
                    this.setFormValue(formValue, field);
                }


            }
        });
        return formValue as AbstractObject;
    }
    
    setFormValue(newValue: unknown, field: Field) {
        if (field.value.includes(".")) {
            const names = field.value.split(".");
            if (names.length > 2) {
                console.error('Cannot process field with multiple object levels', field);
            }
            if (newValue[names[0]] === undefined || newValue[names[0]] === null) {
                newValue[names[0]] = {};
            }
            newValue[names[0]][names[1]] = field.getFormValue();

        } else {
            newValue[field.name] = field.getFormValue();
        }
    }

    get(name: string) {
        return this.getFieldByName(name);
    }

    getFieldByName(name: string) {
        const f = this.fields.find(f => f.name === name);
        if (f === undefined) {
            console.warn('Unable to find field', {seeking: name, in: this.fields});
        }
        return f;
    }

    getFieldByValue(value: string) {
        for (const field of this.fields) {
            if (field.value === value) {
                return field;
            }
        }
        return null;
    }
}

@Component({
    selector: 'app-field-set',
    templateUrl: './field-set.component.html',
    styleUrls: ['./field-set.component.scss'],
    imports: [NgClass, MatTabsModule, CtlHolderComponent, NgTemplateOutlet]
})
export class FieldSetComponent implements AfterViewInit {

    @Input() fieldSet: FieldSet;
    @Input() isPhone: boolean;
    @Input() initComplete: boolean;
    @Input() control: AppFormControl;
    @Input() on: ControlOn = 'form';
    @Input() showAfter = false;
    @Input() isPrint = false;

    hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

    focusRow: RowDef;

    public static make(label: string, value: string, options: Partial<Field> = {}) {
        /* */
        const field: Field = new Field(
            {
                label, value, type: 'fieldSet',
                cellOpts: { style: 'justify-content: flex-end; text-align: right; padding-right: 5px' },
                footer: { style: 'justify-content: flex-end; text-align: right; padding-right: 5px' }
            },
            options
        );
        field.formControlFactory = FieldSetComponent.createComponent;
        return field;
    }

    public static createComponent(vcr: ViewContainerRef, ctl: AppFormControl, on: ControlOn) {
        const componentRef = vcr.createComponent(FieldSetComponent);
        componentRef.instance.control = ctl;
        componentRef.instance.on = on;
        return componentRef;
    }

    getFieldCount(field: Field) {
        if (field instanceof GridField || field instanceof AttachmentField) {
            if (Array.isArray(field.control?.value) && field.control.value.length > 0) {
                return field.control.value.length;
            } else {
                return '';
            }
        } else {
            return '';
        }
    }

    maximiseRow(row: RowDef) {

        if (this.focusRow) {
            console.log('Up ');
            this.focusRow = null;
        } else {
            console.log('Show Sedcond');
            this.focusRow = row;
        }
    }

    showRow(row: RowDef) {
        if (this.focusRow) {

            if (this.focusRow === row) {
                return true;
            } else {
                return false;
            }
        } else if (this.showAfter) {
            return row.showAfter; // only show rows explicitly marked showAfter: true after the save control;
        } else {
            if (!row.showAfter) {
                return true; // Before save control Show all cells not explicitly marked showAfter: true
            }
        }
        return false;
    }

    /* A line is drawn in a cell if any of its members would have been - members share a cell by
       construction, so this is showField for the whole line. */
    showLine(line: Field[], inRow: number, inCol: number) {
        return line.some(field => this.showField(field, inRow, inCol));
    }

    showField(field: Field, inRow: number, inCol: number) {
        if (field) {
            if (this.isPhone && inCol === 1) {
                return true;
            } else if (field.inRowCol(inRow, inCol)) {
                return true;
            }
        }
        return false;
    }

    ngAfterViewInit(): void {
        this.focusFirstInput();
    }

    private focusFirstInput() {
        // Focuses on first element, try rid of Aria Errors, especially on dialogs!
        const focusableSelector = [
            'input:not([disabled]):not([type=hidden])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            '[tabindex]:not([tabindex="-1"])'
        ].join(',');

        const element = this.hostElement.nativeElement.querySelector<HTMLElement>(focusableSelector);
        if (element) {
            // Optional: delay to ensure view is fully rendered
            requestAnimationFrame(() => element.focus());
        }
    }
}
